import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import styles from './TableHeaderCell.module.scss'

const POPOVER_WIDTH = 240
const POPOVER_MIN_HEIGHT = 80
const VIEWPORT_MARGIN = 8
const GAP = 4

interface PopoverPos {
  top: number
  left: number
  width: number
}

export interface TableHeaderCellProps {
  title: string
  filter?: React.ReactNode
  className?: string
}

export default function TableHeaderCell({ title, filter, className }: TableHeaderCellProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<PopoverPos | null>(null)
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return
    const compute = () => {
      const rect = anchorRef.current!.getBoundingClientRect()
      const maxWidth = Math.min(POPOVER_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2)
      let left = rect.left
      if (left + maxWidth > window.innerWidth - VIEWPORT_MARGIN) {
        left = Math.max(VIEWPORT_MARGIN, window.innerWidth - maxWidth - VIEWPORT_MARGIN)
      }
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN
      let top = rect.bottom + GAP
      if (top + POPOVER_MIN_HEIGHT > window.innerHeight - VIEWPORT_MARGIN) {
        const flipped = rect.top - POPOVER_MIN_HEIGHT - GAP
        top = flipped >= VIEWPORT_MARGIN ? flipped : VIEWPORT_MARGIN
      }
      setPos({ top, left, width: maxWidth })
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      const target = e.target as Node
      if (popoverRef.current?.contains(target)) return
      if (anchorRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  if (!filter) {
    return <span className={className}>{title}</span>
  }

  return (
    <>
      <button
        type="button"
        ref={anchorRef}
        className={`${styles.trigger} ${className ?? ''}`}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((o) => !o)
        }}
      >
        <span>{title}</span>
        <span className={styles.chevron} aria-hidden>
          ▾
        </span>
      </button>
      {open && pos && (
        <div
          ref={popoverRef}
          className={styles.popover}
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.popoverHeader}>
            <span className={styles.popoverTitle}>{title}</span>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => setOpen(false)}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
          <div className={styles.popoverBody}>{filter}</div>
        </div>
      )}
    </>
  )
}
